/**
 * Wave 6 (2026-09-11, HCSS Head-to-Head) — My Sheets.
 * A superintendent's own starred sheets across every project, newest
 * first. Not a project browser — just their personal quick-jump list.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Loader2, ArrowRight, AlertTriangle, RefreshCw } from "lucide-react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

export default function MySheets() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // GH-13 (real-plan certification, 2026-09-30) — a failed read used to
  // reset `rows` to `[]`, rendering the SAME "No starred sheets yet"
  // copy as a genuinely empty list — real starred sheets could appear
  // to have vanished on a transient network blip. `rows` is no longer
  // touched on failure; this `loadError` flag alone decides which
  // empty-looking state is shown.
  const [loadError, setLoadError] = useState(false);
  // GH-13 — bump to force a retry without duplicating the fetch logic.
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/me/favorites")
      .then((r) => { if (!cancelled) { setRows(r.data || []); setLoadError(false); } })
      .catch(() => { if (!cancelled) setLoadError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [retryNonce]);

  const unfavorite = async (sheetId) => {
    try {
      await api.delete(`/sheets/${sheetId}/favorite`);
      setRows((prev) => prev.filter((r) => r.sheet_id !== sheetId));
    } catch {
      confirm.saveFailed("Couldn't remove from My Sheets");
    }
  };

  return (
    <Shell>
      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="my-sheets-page">
        <div className="flex items-center gap-2 mb-1">
          <Star size={18} className="text-[var(--fop-warning)]" fill="currentColor" />
          <h1 className="text-xl font-semibold text-[var(--fop-text)]">My Sheets</h1>
        </div>
        <p className="text-sm text-[var(--fop-text-3)] mb-5">
          The sheets you come back to every day — starred from any sheet viewer.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-[var(--fop-text-3)] text-sm" data-testid="my-sheets-loading">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : loadError ? (
          <div className="text-center py-16 border border-dashed border-[var(--fop-warn)] rounded-lg bg-[rgba(234,179,8,0.06)]" data-testid="my-sheets-load-error">
            <AlertTriangle size={28} className="mx-auto text-[var(--fop-warn)] mb-2" />
            <p className="text-sm text-[var(--fop-text-2)] font-medium">
              Couldn't load your starred sheets
            </p>
            <p className="text-xs text-[var(--fop-text-3)] mt-1">
              This is a connection problem, not confirmation your list is empty.
            </p>
            <button
              onClick={() => setRetryNonce((n) => n + 1)}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[var(--fop-surface-2)] border border-[var(--fop-border)] text-xs font-mono uppercase tracking-wider text-[var(--fop-text)]"
              data-testid="my-sheets-retry"
              style={{ minHeight: 44 }}
            >
              <RefreshCw size={12} /> Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[var(--fop-border)] rounded-lg" data-testid="my-sheets-empty">
            <Star size={28} className="mx-auto text-[var(--fop-text-4)] mb-2" />
            <p className="text-sm text-[var(--fop-text-3)]">
              No starred sheets yet. Open any sheet and tap the star in the toolbar.
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="my-sheets-list">
            {rows.map((r) => (
              <div
                key={r.favorite_id}
                className="group flex items-center gap-3 p-3 rounded-lg bg-[var(--fop-surface-1)] border border-[var(--fop-border)] hover:border-[var(--fop-border-hi)] cursor-pointer transition-colors"
                onClick={() => navigate(`/sheet/${r.sheet_id}`)}
                data-testid={`my-sheets-row-${r.sheet_id}`}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); unfavorite(r.sheet_id); }}
                  className="shrink-0"
                  data-testid={`my-sheets-unstar-${r.sheet_id}`}
                  title="Remove from My Sheets"
                >
                  <Star size={16} className="text-[var(--fop-warning)]" fill="currentColor" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-[var(--fop-text)]" data-testid={`my-sheets-number-${r.sheet_id}`}>
                      {r.sheet_number}
                    </span>
                    {!r.is_current && (
                      <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--st-escalated)]/15 text-[var(--st-escalated)]">
                        Superseded
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-[var(--fop-text-2)] truncate">{r.sheet_title}</div>
                  <div className="text-xs text-[var(--fop-text-3)] mt-0.5 truncate">
                    {r.project_name} {r.project_number ? `· ${r.project_number}` : ""} · starred {fmtDate(r.favorited_at)}
                  </div>
                </div>
                <ArrowRight size={15} className="shrink-0 text-[var(--fop-text-4)] group-hover:text-[var(--fop-primary)] transition-colors" />
              </div>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
